import { useCallback } from "react";

export default function ManagementActions({
  userDraftById,
  toUserDraft,
  setUserSavingId,
  canManageUserAccount,
  setAdminError,
  texts,
  normalizeUserStatus,
  ASSIGNABLE_ROLES,
  adminFetch,
  apiBaseUrl,
  readApiJson,
  setAdminUsers,
  setUserDraftById,
  setAdminToast,
  normalizeAdminPermissionMessage
}) {
  const saveUserManagement = useCallback(async (userItem, draftOverride = null) => {
    const draft = draftOverride || userDraftById[userItem.id] || toUserDraft(userItem);
    setUserSavingId(userItem.id);
    try {
      if (!canManageUserAccount(userItem)) {
        setAdminError(texts.userManagementRestrictedHint || "You can only manage users with a lower role than your own.");
        return;
      }

      const currentName = String(userItem.name || "").trim();
      const currentEmail = String(userItem.email || "").trim();
      const currentPhone = String(userItem.phone || "").trim();
      const currentStatus = normalizeUserStatus(userItem.status);
      const currentJobTitle = String(userItem.jobTitle || "").trim();
      const currentRole = String(userItem.role || "customer").trim() || "customer";
      const nextRole = String(draft.role || currentRole).trim() || currentRole;
      if (draft.role && !ASSIGNABLE_ROLES.includes(nextRole)) {
        setAdminError("Invalid role value");
        return;
      }
      const requestedStatus = normalizeUserStatus(draft.status || "active");
      const statusChanged = requestedStatus !== currentStatus;

      const requestPayload = {};
      if (nextRole !== currentRole) {
        requestPayload.role = nextRole;
      }
      if (String(draft.name || "").trim() !== currentName) {
        requestPayload.name = draft.name;
      }
      if (String(draft.email || "").trim() !== currentEmail) {
        requestPayload.email = draft.email;
      }
      if (String(draft.phone || "").trim() !== currentPhone) {
        requestPayload.phone = draft.phone;
      }
      if (statusChanged) {
        requestPayload.status = requestedStatus;
      }
      if (String(draft.jobTitle || "").trim() !== currentJobTitle) {
        requestPayload.jobTitle = draft.jobTitle;
      }

      if (!Object.keys(requestPayload).length) {
        setAdminError("");
        return;
      }

      const requestBody = JSON.stringify(requestPayload);

      const endpointPrimary = `${apiBaseUrl}/admin/users/${userItem.id}`;
      const endpointCompat = `${apiBaseUrl}/admin/users/${userItem.id}/position`;
      const modernPlan = [
        { url: endpointPrimary, method: "PATCH" },
        { url: endpointPrimary, method: "POST" }
      ];
      const compatPlan = [
        { url: endpointCompat, method: "PATCH" },
        { url: endpointCompat, method: "POST" }
      ];
      const hasExtendedFieldChange = Object.keys(requestPayload).some((key) => key !== "role");
      const attemptPlan = hasExtendedFieldChange
        ? [
            modernPlan[0],
            compatPlan[0],
            modernPlan[1],
            compatPlan[1]
          ]
        : [...modernPlan, ...compatPlan];

      let response = null;
      for (let index = 0; index < attemptPlan.length; index += 1) {
        const attempt = attemptPlan[index];
        // eslint-disable-next-line no-await-in-loop
        response = await adminFetch(attempt.url, {
          method: attempt.method,
          headers: { "Content-Type": "application/json" },
          body: requestBody
        });

        if (response.ok) {
          break;
        }

        if (![404, 405, 501].includes(response.status)) {
          break;
        }
      }

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Position assignment failed");
      }

      const normalizedReturnedStatus = normalizeUserStatus(data?.user?.status || currentStatus);
      if (statusChanged && requestedStatus !== normalizedReturnedStatus) {
        throw new Error("保存请求已返回成功，但账号状态未确认落库。请重启后端后重试。");
      }

      setAdminUsers((prev) =>
        prev.map((item) =>
          item.id === userItem.id
            ? {
                ...item,
                ...(data.user || {}),
                role: (data.user && data.user.role) || nextRole
              }
            : item
        )
      );
      setUserDraftById((prev) => ({
        ...prev,
        [userItem.id]: toUserDraft({
          ...userItem,
          ...(data.user || {}),
          role: (data.user && data.user.role) || nextRole,
          status: normalizedReturnedStatus
        })
      }));
      setAdminToast(`${texts.positionMaintenance} ${texts.save}`);
      setAdminError("");

      const expectedSnapshot = {
        id: Number(userItem.id),
        name: String(draft.name || "").trim(),
        email: String(draft.email || "").trim().toLowerCase(),
        jobTitle: String(draft.jobTitle || "").trim(),
        status: requestedStatus
      };

      window.setTimeout(async () => {
        try {
          const latestResponse = await adminFetch(`${apiBaseUrl}/admin/users`).then(readApiJson);
          const latestUsers = Array.isArray(latestResponse?.users) ? latestResponse.users : [];
          const latestUser = latestUsers.find((item) => Number(item?.id) === expectedSnapshot.id);
          if (!latestUser) {
            return;
          }

          const latestStatus = normalizeUserStatus(latestUser.status);
          const hasConflict =
            String(latestUser.name || "").trim() !== expectedSnapshot.name ||
            String(latestUser.email || "").trim().toLowerCase() !== expectedSnapshot.email ||
            String(latestUser.jobTitle || "").trim() !== expectedSnapshot.jobTitle ||
            latestStatus !== expectedSnapshot.status;

          if (!hasConflict) {
            return;
          }

          setAdminUsers((prev) =>
            prev.map((item) => (Number(item?.id) === expectedSnapshot.id ? { ...item, ...latestUser } : item))
          );
          setUserDraftById((prev) => ({
            ...prev,
            [expectedSnapshot.id]: toUserDraft({
              ...latestUser,
              role: String(latestUser.role || nextRole || "customer").trim() || "customer",
              status: latestStatus
            })
          }));
          setAdminToast(texts.userDataResyncedHint || "Profile fields were resynced from server after save.");
        } catch (_err) {
          // Silent refresh is best-effort and should not block normal save flow.
        }
      }, 2000);
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(normalizeAdminPermissionMessage(err.message, "Position assignment failed"));
      }
    } finally {
      setUserSavingId(0);
    }
  }, [
    ASSIGNABLE_ROLES,
    adminFetch,
    apiBaseUrl,
    canManageUserAccount,
    normalizeAdminPermissionMessage,
    normalizeUserStatus,
    readApiJson,
    setAdminError,
    setAdminToast,
    setAdminUsers,
    setUserDraftById,
    setUserSavingId,
    texts.positionMaintenance,
    texts.save,
    texts.userDataResyncedHint,
    texts.userManagementRestrictedHint,
    toUserDraft,
    userDraftById
  ]);

  return { saveUserManagement };
}